import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

const AUTH_STORAGE_KEY = '__AUTH__KEY';

/**
 * 认证 Store 的返回类型定义
 */
interface AuthStore {
  /** 微信授权码 - 响应式引用 */
  code: ComputedRef<string>;
  /** 内部微信授权码 - 响应式引用 */
  _code: Ref<string>;
  /** 设置授权码 */
  setCode: (value: string) => void;
  /** 获取并清除授权码（一次性使用） */
  getCode: () => string;
}
/**
 * 微信授权码 Store 所有需要获取 code 的场景都需要使用这个 store
 * 用于管理微信授权码的状态和操作
 */
export const useAuthStore = defineStore(
  AUTH_STORAGE_KEY,
  (): AuthStore => {
    /**
     * 内部code 外部不应该直接访问
     * 微信授权码的响应式引用
     */
    const _code = ref<string>('');

    const code = computed(() => _code.value);

    /**
     * 设置授权码
     * @param value - 授权码值
     */
    const setCode = (value: string): void => {
      _code.value = value;
    };

    /**
     * 获取并清除code 只有一次有效
     * @returns 当前的授权码值
     */
    const getCode = (): string => {
      try {
        return _code.value;
      } finally {
        _code.value = '';
      }
    };

    return {
      _code, // 为什么要暴露内部code, 因为使用了缓存库，需要在缓存中存储code
      code,
      setCode,
      getCode,
    };
  },
  {
    persist: {
      key: AUTH_STORAGE_KEY,
      storage: localStorage,
    },
  },
);
